import React, { useCallback, useContext, useEffect, useRef, useState } from 'react'

import {
  useFormDataV2,
  useFormValidation,
  validateEmailWithLocale,
  validateIsNotBlankWithLocale,
  validateNewPasswordWithLocale,
} from '@monorepo/utils'

import Input from '../Input'
import { ContextApp } from '@monorepo/service'
import { ApiCore, mapLanguageToApi, useRegister } from '@monorepo/api'
import RegisterButton from '../RegisterButton'
import PasswordInputToggle from '../PasswordInputToggle'
import { getRegistrationBonus } from '../../constants'
import { UserAlreadyExists } from '@monorepo/uikit'
import { useGeoRuntime } from '../../geo/useGeoRuntime'
import { GEO_META, type SupportedGeo } from '../../geo/config'
import { useFormGeo } from '../../hooks/useFormGeo'
import { FormCurrency } from '../FormCurrency'

declare const window: Window & typeof globalThis & { updateContent?: any }

const VALIDATORS = {
  rules: {
    email: [validateIsNotBlankWithLocale, validateEmailWithLocale],
    password: [validateIsNotBlankWithLocale, validateNewPasswordWithLocale],
  },
  defaultData: {
    email: '',
    password: '',
  },
}

const FormEmail: React.FC = () => {
  const { sid, setDataRegister, isCdaBalancerInfoLoading, cdaBalancerInfo } = useContext(ContextApp)

  const { allowedRegions, getCopyKey, syncGeoState } = useGeoRuntime()

  const { formGeo, formGeoConfig, handleFormGeoChange } = useFormGeo()

  const bonus = getRegistrationBonus(formGeo)

  const { handleRequest, commonErrorMsg, fieldErrorMsg, isLoading } = useRegister({
    sid,
    bonusId: bonus?.bonusId,
    setDataRegister,
  })

  const { validateForm } = useFormValidation(VALIDATORS.rules)

  const { form, onValidate, onInputChange } = useFormDataV2({
    validateForm,
    defaultData: VALIDATORS.defaultData,
  })

  const emailErrorMessage = fieldErrorMsg?.email
  const passwordErrorMessage = fieldErrorMsg?.password

  const [isOpen, setIsOpen] = useState(false)
  const pickerRef = useRef<HTMLDivElement>(null)
  const hasMultipleGeoOptions = allowedRegions.length > 1

  const handleGeoSelect = useCallback(
    (nextGeo: SupportedGeo) => {
      handleFormGeoChange(nextGeo)
      setIsOpen(false)
    },
    [handleFormGeoChange, formGeo],
  )

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (!pickerRef.current?.contains(e.target as Node)) {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  const handleSubmit = useCallback(() => {
    onValidate(
      'email',
      'password',
    )((data) => {
      const nextGeoState = syncGeoState(formGeo)

      const payload = {
        account_registered_by: ApiCore.PublicAccountRegistrationTypeEnum.Email,
        language: mapLanguageToApi(nextGeoState.language),
        country_of_residence: formGeoConfig.countryOfResidence,
        currency: formGeoConfig.currency,
        privacy_policy: true,
        terms_and_conditions: true,
        receive_promos_enabled: true,
        ...data,
      }
      console.log('Полный payload:', payload)

      handleRequest(payload)
    })
  }, [formGeo, formGeoConfig, handleRequest, onValidate, syncGeoState])

  const fields = ['email', 'password']
  const touched = fields.some((f) => form.statuses[f as keyof typeof form.statuses])
  const isFormValid = fields.every(
    (f) => form.statuses[f as keyof typeof form.statuses] === 'valid',
  )

  useEffect(() => {
    window.updateContent?.()
  }, [form.errors, formGeo])

  return (
    <>
      <div
        className={`ejForm__fields ${touched ? (isFormValid ? 'validForm' : 'invalidForm') : ''}`}
      >
        <Input
          id="email"
          type="email"
          className="input"
          data-i18n-placeholder={getCopyKey('hero.placeholders.email')}
          placeholder="Email"
          value={form.data.email}
          errorMsg={emailErrorMessage ?? form.errors.email}
          inputMode="email"
          onChange={(e) =>
            onInputChange('email')({
              target: { value: e.target.value },
            } as any)
          }
          prefixIcon={
            <div ref={pickerRef} className="geoPrefix">
              <button
                type="button"
                className={`geoPrefix__button${isOpen ? ' active' : ''}`}
                onClick={() => hasMultipleGeoOptions && setIsOpen((v) => !v)}
              >
                <img
                  src={GEO_META[formGeo].flag}
                  className="geoIcon"
                  alt={GEO_META[formGeo].label}
                />
                {hasMultipleGeoOptions && <span className="geoPrefix__caret" />}
              </button>

              {hasMultipleGeoOptions && isOpen && (
                <div className="geoPrefix__menu">
                  {allowedRegions.map((g) => (
                    <button
                      key={g}
                      type="button"
                      className={`geoPrefix__option${g === formGeo ? ' active' : ''}`}
                      onClick={() => handleGeoSelect(g)}
                    >
                      <img src={GEO_META[g].flag} alt={GEO_META[g].label} />
                      <span>{GEO_META[g].label}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          }
        />

        <PasswordInputToggle
          value={form.data.password}
          onChange={(val) =>
            onInputChange('password')({
              target: { value: val },
            } as any)
          }
          dataI18nPlaceholder={getCopyKey('hero.placeholders.password')}
          errorMsg={commonErrorMsg ?? passwordErrorMessage ?? form.errors.password}
        />

        <UserAlreadyExists
          errorMessage={commonErrorMsg || ''}
          loginType="email"
          isBalancerInfoLoading={isCdaBalancerInfoLoading}
          balancerInfo={cdaBalancerInfo}
        />
        <FormCurrency selectedGeo={formGeo} />
      </div>

      <RegisterButton handleSubmit={handleSubmit} disabled={isLoading} />
    </>
  )
}

export default FormEmail
